import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const BlueprintDownload = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    organizationSize: '',
    industry: '',
    phoneNumber: '',
    agreeToTerms: false,
    subscribeNewsletter: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const organizationSizes = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'small', label: 'Small Business (51-200 employees)' },
    { value: 'medium', label: 'Medium Enterprise (201-1000 employees)' },
    { value: 'large', label: 'Large Enterprise (1000+ employees)' }
  ];

  const industries = [
    { value: 'technology', label: 'Technology & Software' },
    { value: 'finance', label: 'Financial Services' },
    { value: 'healthcare', label: 'Healthcare & Life Sciences' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'consulting', label: 'Professional Services' },
    { value: 'education', label: 'Education' },
    { value: 'government', label: 'Government & Public Sector' },
    { value: 'other', label: 'Other Industry' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.company?.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (!formData?.jobTitle?.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }

    if (!formData?.organizationSize) {
      newErrors.organizationSize = 'Please select organization size';
    }

    if (!formData?.industry) {
      newErrors.industry = 'Please select your industry';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIsSubmitted(true);
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-xl shadow-executive p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircle" size={32} className="text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-text-primary mb-4">Download Ready!</h3>
        <p className="text-text-secondary mb-6 leading-relaxed">
          Thank you for your interest in the AI Core Blueprint. Your download link has been sent to{' '}
          <span className="font-medium text-text-primary">{formData?.email}</span>
        </p>
        <div className="space-y-4">
          <Button
            variant="default"
            className="bg-brand-primary hover:bg-brand-primary/90"
            iconName="Download"
            iconPosition="left"
            fullWidth
          >
            Download AI Core Blueprint (PDF)
          </Button>
          <Button
            variant="outline"
            iconName="Calendar"
            iconPosition="left"
            fullWidth
          >
            Schedule Implementation Consultation
          </Button>
        </div>
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <Icon name="Info" size={16} className="inline mr-2" />
            Check your email for additional resources and next steps
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-executive overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-primary to-brand-secondary p-8 text-white">
        <div className="text-center">
          <Icon name="Download" size={48} className="mx-auto mb-4 opacity-90" />
          <h3 className="text-2xl font-bold mb-2">Download the Complete AI Core Blueprint</h3>
          <p className="text-blue-100 leading-relaxed">
            Get instant access to our comprehensive 47-page methodology guide, implementation templates, 
            and ROI calculation frameworks used by Fortune 500 companies.
          </p>
        </div>
      </div>
      {/* Preview Benefits */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <h4 className="font-semibold text-text-primary mb-4">What's Included:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            'Complete 5-phase methodology framework',
            'Implementation timeline templates',
            'ROI calculation worksheets',
            'Risk assessment checklists',
            'Technology stack recommendations',
            'Change management playbooks'
          ]?.map((item, index) => (
            <div key={index} className="flex items-center space-x-3">
              <Icon name="CheckCircle" size={16} className="text-green-500 flex-shrink-0" />
              <span className="text-sm text-text-secondary">{item}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Form */}
      <form onSubmit={handleSubmit} className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="First Name"
            type="text"
            placeholder="Enter your first name"
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            required
          />

          <Input
            label="Last Name"
            type="text"
            placeholder="Enter your last name"
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            error={errors?.lastName}
            required
          />

          <Input
            label="Business Email"
            type="email"
            placeholder="Enter your business email"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />

          <Input
            label="Phone Number"
            type="tel"
            placeholder="Enter your phone number"
            value={formData?.phoneNumber}
            onChange={(e) => handleInputChange('phoneNumber', e?.target?.value)}
          />

          <Input
            label="Company Name"
            type="text"
            placeholder="Enter your company name"
            value={formData?.company}
            onChange={(e) => handleInputChange('company', e?.target?.value)}
            error={errors?.company}
            required
          />

          <Input
            label="Job Title"
            type="text"
            placeholder="Enter your job title"
            value={formData?.jobTitle}
            onChange={(e) => handleInputChange('jobTitle', e?.target?.value)}
            error={errors?.jobTitle}
            required
          />

          <Select
            label="Organization Size"
            placeholder="Select organization size"
            options={organizationSizes}
            value={formData?.organizationSize}
            onChange={(value) => handleInputChange('organizationSize', value)}
            error={errors?.organizationSize}
            required
          />

          <Select
            label="Industry"
            placeholder="Select your industry"
            options={industries}
            value={formData?.industry}
            onChange={(value) => handleInputChange('industry', value)}
            error={errors?.industry}
            required
          />
        </div>

        <div className="mt-6 space-y-4">
          <Checkbox
            label="I agree to the Terms of Service and Privacy Policy"
            checked={formData?.agreeToTerms}
            onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
            error={errors?.agreeToTerms}
            required
          />

          <Checkbox
            label="Subscribe to AI insights newsletter (recommended)"
            description="Get weekly insights on AI trends, implementation strategies, and industry best practices"
            checked={formData?.subscribeNewsletter}
            onChange={(e) => handleInputChange('subscribeNewsletter', e?.target?.checked)}
          />
        </div>

        <div className="mt-8">
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isSubmitting}
            className="bg-brand-primary hover:bg-brand-primary/90"
            iconName="Download"
            iconPosition="left"
          >
            {isSubmitting ? 'Processing...' : 'Download AI Core Blueprint'}
          </Button>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-text-secondary">
            By downloading, you agree to receive occasional emails about AI implementation strategies. 
            Unsubscribe anytime.
          </p>
        </div>
      </form>
    </div>
  );
};

export default BlueprintDownload;